<template>
  <div class="statistics-container">
    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p class="text-gray-600 mt-4">Chargement des statistiques...</p>
    </div>

    <!-- Statistiques -->
    <div v-else>
      <!-- Statistiques globales -->
      <div class="stats-section">
        <div class="section-header">
          <h3 class="section-title">
            <i class="fas fa-chart-bar mr-2"></i>Statistiques Globales
          </h3>
          <button @click="$emit('refresh')" class="refresh-btn">
            <i class="fas fa-sync-alt"></i> Actualiser
          </button>
        </div>
        
        <div v-if="globalStats && globalStats.total_requests >= 0" class="global-stats-grid">
          <div class="stat-card-large stat-primary">
            <div class="stat-icon-large">
              <i class="fas fa-clipboard-list"></i>
            </div>
            <div class="stat-info">
              <p class="stat-value-large">{{ formatNumber(globalStats.total_requests) }}</p>
              <p class="stat-label-large">Total Requêtes</p>
            </div>
          </div>

          <div class="stat-card-large stat-success">
            <div class="stat-icon-large">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="stat-info">
              <p class="stat-value-large">{{ formatNumber(globalStats.success_count) }}</p>
              <p class="stat-label-large">Succès ({{ successRate }}%)</p>
            </div>
          </div>

          <div class="stat-card-large stat-error">
            <div class="stat-icon-large">
              <i class="fas fa-times-circle"></i>
            </div>
            <div class="stat-info">
              <p class="stat-value-large">{{ formatNumber(globalStats.failure_count) }}</p>
              <p class="stat-label-large">Échecs ({{ failureRate }}%)</p>
            </div>
          </div>

          <div class="stat-card-large stat-time">
            <div class="stat-icon-large">
              <i class="fas fa-tachometer-alt"></i>
            </div>
            <div class="stat-info">
              <p class="stat-value-large">{{ formatResponseTime(globalStats.avg_response_time) }} ms</p>
              <p class="stat-label-large">Temps Moyen</p>
              <p class="stat-detail-large">
                Min: {{ globalStats.min_response_time }}ms | Max: {{ globalStats.max_response_time }}ms
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Endpoints -->
      <div class="stats-section">
        <div class="section-header">
          <h3 class="section-title">
            <i class="fas fa-server mr-2"></i>Top Endpoints
          </h3>
        </div>
        <div class="table-card">
          <table class="stats-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Requêtes</th>
                <th>Succès</th>
                <th>Échecs</th>
                <th>Temps Moyen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="topEndpoints.length === 0">
                <td colspan="5" class="no-data">Aucune donnée disponible</td>
              </tr>
              <tr v-for="(endpoint, index) in topEndpoints" :key="index">
                <td>
                  <code class="endpoint-code">{{ endpoint.endpoint }}</code>
                </td>
                <td class="font-semibold">{{ formatNumber(endpoint.request_count) }}</td>
                <td>
                  <span class="text-green-600 font-semibold">{{ formatNumber(endpoint.success_count) }}</span>
                </td>
                <td>
                  <span class="text-red-600 font-semibold">{{ formatNumber(endpoint.failure_count) }}</span>
                </td>
                <td>{{ formatResponseTime(endpoint.avg_response_time) }} ms</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Top Erreurs -->
      <div class="stats-section">
        <div class="section-header">
          <h3 class="section-title">
            <i class="fas fa-exclamation-triangle mr-2"></i>Top Erreurs
          </h3>
        </div>
        <div class="table-card">
          <table class="stats-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Code HTTP</th>
                <th>Nombre d'erreurs</th>
                <th>Dernière occurrence</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="topErrors.length === 0">
                <td colspan="4" class="no-data">Aucune erreur enregistrée</td>
              </tr>
              <tr v-for="(error, index) in topErrors" :key="index">
                <td>
                  <code class="endpoint-code">{{ error.endpoint }}</code>
                </td>
                <td>
                  <span :class="['http-code', getHttpCodeClass(error.status_code)]">
                    {{ error.status_code }}
                  </span>
                </td>
                <td>
                  <span class="error-count">{{ formatNumber(error.error_count) }}</span>
                </td>
                <td class="text-sm text-gray-600">
                  {{ formatDateTime(error.last_occurrence) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Top Utilisateurs -->
      <div class="stats-section">
        <div class="section-header">
          <h3 class="section-title">
            <i class="fas fa-users mr-2"></i>Top Utilisateurs
          </h3>
        </div>
        <div class="table-card">
          <table class="stats-table">
            <thead>
              <tr>
                <th>ID Utilisateur</th>
                <th>Requêtes</th>
                <th>Erreurs</th>
                <th>Dernière Activité</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="topUsers.length === 0">
                <td colspan="4" class="no-data">Aucune donnée disponible</td>
              </tr>
              <tr v-for="(user, index) in topUsers" :key="index">
                <td>
                  <div class="user-cell">
                    <i class="fas fa-user-circle mr-2 text-gray-400"></i>
                    <span class="font-mono text-sm">{{ user.user_id }}</span>
                  </div>
                </td>
                <td class="font-semibold">{{ formatNumber(user.request_count) }}</td>
                <td>
                  <span :class="user.error_count > 0 ? 'text-red-600 font-semibold' : 'text-green-600'">
                    {{ formatNumber(user.error_count) }}
                  </span>
                </td>
                <td class="text-sm text-gray-600">
                  {{ formatDateTime(user.last_activity) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogsStatistics',
  props: {
    globalStats: Object,
    topEndpoints: {
      type: Array,
      default: () => [],
    },
    topErrors: {
      type: Array,
      default: () => [],
    },
    topUsers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['refresh'],
  computed: {
    successRate() {
      if (!this.globalStats || this.globalStats.total_requests === 0) return 0;
      return ((this.globalStats.success_count / this.globalStats.total_requests) * 100).toFixed(1);
    },
    failureRate() {
      if (!this.globalStats || this.globalStats.total_requests === 0) return 0;
      return ((this.globalStats.failure_count / this.globalStats.total_requests) * 100).toFixed(1);
    },
  },
  methods: {
    formatNumber(num) {
      return new Intl.NumberFormat('fr-FR').format(num || 0);
    },
    formatDateTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatResponseTime(time) {
      const numTime = parseFloat(time);
      if (isNaN(numTime)) return 0;
      return numTime.toFixed(1);
    },
    getHttpCodeClass(code) {
      if (code >= 400 && code < 500) return 'client-error';
      if (code >= 500) return 'server-error';
      return '';
    },
  },
};
</script>

<style scoped>
.statistics-container {
  padding: 1rem 0;
}

.loading-container {
  padding: 4rem;
  text-align: center;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #16a34a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.stats-section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.refresh-btn {
  padding: 0.5rem 1rem;
  background-color: #16a34a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s;
}

.refresh-btn:hover {
  background-color: #15803d;
}

.global-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card-large {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: transform 0.2s;
}

.stat-card-large:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
}

.stat-icon-large {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
}

.stat-primary .stat-icon-large {
  background-color: #dbeafe;
  color: #2563eb;
}

.stat-success .stat-icon-large {
  background-color: #dcfce7;
  color: #16a34a;
}

.stat-error .stat-icon-large {
  background-color: #fee2e2;
  color: #dc2626;
}

.stat-time .stat-icon-large {
  background-color: #fef3c7;
  color: #f59e0b;
}

.stat-info {
  flex: 1;
}

.stat-value-large {
  font-size: 2.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.stat-label-large {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.5rem;
  font-weight: 500;
}

.stat-detail-large {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.25rem;
}

.table-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
}

.stats-table thead {
  background-color: #f9fafb;
}

.stats-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stats-table td {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
  color: #374151;
}

.stats-table tbody tr:hover {
  background-color: #f9fafb;
}

.endpoint-code {
  background-color: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
  color: #374151;
}

.http-code {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.75rem;
}

.http-code.client-error {
  background-color: #fef3c7;
  color: #f59e0b;
}

.http-code.server-error {
  background-color: #fee2e2;
  color: #dc2626;
}

.error-count {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
}

.user-cell {
  display: flex;
  align-items: center;
}

.no-data {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}
</style>

